import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

export default function TicketInbox() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTickets();
  }, [filter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const url = filter === 'ALL' 
        ? `${BASE_URL}/api/wip-dashboard/tickets` 
        : `${BASE_URL}/api/wip-dashboard/tickets?status=${filter}`;
      
      const res = await axios.get(url, { withCredentials: true });
      if (res.data.success) {
        setTickets(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
      Swal.fire("Error", "Failed to fetch tickets.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, action) => {
    try {
      let body = {};
      if (action === 'acknowledge') {
        const result = await Swal.fire({ title: 'Acknowledge ticket', input: 'textarea', inputPlaceholder: 'Action plan and assigned workers', showCancelButton: true, inputValidator: value => !value ? 'Action plan is required' : undefined });
        if (!result.isConfirmed) return;
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        body = { acknowledged_by: user.Name || user.EmployeeID || 'SUPERVISOR', action_plan: result.value };
      }
      const res = await axios.post(`${BASE_URL}/api/wip-dashboard/tickets/${id}/${action}`, body, { withCredentials: true });
      if (res.data.success) {
        Swal.fire("Success", res.data.message, "success");
        fetchTickets();
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || `Error executing action: ${action}`, "error");
    }
  };

  const badgeClass = (status) => {
    if (status === 'TEMPORARY') return { bg: '#fee2e2', color: '#b91c1c' }; // red
    if (status === 'ACKNOWLEDGED') return { bg: '#e0f2fe', color: '#0284c7' }; // light blue
    if (status === 'READY_FOR_VERIFICATION') return { bg: '#fef3c7', color: '#b45309' }; // orange
    if (status === 'VERIFIED_CLOSED') return { bg: '#dcfce7', color: '#15803d' }; // green
    if (status === 'SLA_BREACHED') return { bg: '#f1f5f9', color: '#334155' }; // slate
    return { bg: '#f1f5f9', color: '#64748b' };
  };

  const filteredTickets = tickets.filter(t => {
    const matchSearch = t.lot_id?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                        t.batch_id?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchSearch;
  });

  return (
    <div className="d-flex dashboard">
      <style>{`
        .wip-ticket-table th {
          background-color: #ffffff !important;
          color: #334155 !important;
          border-bottom: 2px solid #e2e8f0 !important;
          font-weight: 600 !important;
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 16px !important;
        }
        .wip-ticket-table td {
          padding: 16px !important;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .wip-ticket-table tbody tr {
          transition: all 0.2s ease;
        }
        .wip-ticket-table tbody tr:hover {
          background-color: #f8fafc;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }
        .filter-tab {
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid transparent;
        }
        .filter-tab.active {
          background-color: #0ea5e9;
          color: white;
          box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.2);
        }
        .filter-tab.inactive {
          background-color: #f1f5f9;
          color: #64748b;
        }
        .filter-tab.inactive:hover {
          background-color: #e2e8f0;
          color: #334155;
        }
      `}</style>

      {/* ── SIDEBAR ── */}
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        {/* ── HEADER ── */}
        <Header toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} isSidebarVisible={isSidebarVisible} />

        {/* ── MAIN CONTENT AREA ── */}
        <main className="main-container p-0 pb-4" style={{ background: "#f8fafc", fontFamily: "'Inter', sans-serif", minHeight: "calc(100vh - 60px)" }}>
          
          {/* ── PAGE BANNER ── */}
          <div style={{
            background: "linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%)", // light blue gradient
            padding: "32px 40px", color: "#fff",
            boxShadow: "0 4px 12px rgba(14, 165, 233, 0.15)"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center" }}>
                  <i className="bi bi-inbox-fill me-3"></i> Discrepancy Ticket Inbox
                  <span style={{ marginLeft: 16, padding: "4px 12px", background: "#fff", color: "#0284c7", fontSize: 13, borderRadius: 20, fontWeight: 700 }}>
                    12-Hour SLA
                  </span>
                </h1>
                <p style={{ margin: "6px 0 0", opacity: 0.9, fontSize: 15, fontWeight: 500 }}>
                  Manage and resolve handover shortages, quality defects, and process issues.
                </p>
              </div>
              <button
                onClick={fetchTickets}
                style={{
                  background: "rgba(255,255,255,0.2)", color: "#fff",
                  border: "1px solid rgba(255,255,255,0.4)",
                  borderRadius: 10, padding: "10px 20px",
                  fontSize: 14, fontWeight: 600, cursor: "pointer",
                  backdropFilter: "blur(10px)", transition: "all 0.2s",
                  display: "flex", alignItems: "center", gap: 8
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.3)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
              >
                <i className="bi bi-arrow-clockwise"></i> Refresh Inbox
              </button>
            </div>
          </div>

          <div style={{ padding: "32px 40px" }}>
            
            {/* ── TOOLBAR (Search & Filters) ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "16px" }}>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <div className={`filter-tab ${filter === 'ALL' ? 'active' : 'inactive'}`} onClick={() => setFilter('ALL')}>
                  All Open
                </div>
                <div className={`filter-tab ${filter === 'TEMPORARY' ? 'active' : 'inactive'}`} onClick={() => setFilter('TEMPORARY')} style={{ borderLeft: filter === 'TEMPORARY' ? 'none' : '4px solid #ef4444' }}>
                  New / Temp
                </div>
                <div className={`filter-tab ${filter === 'ACKNOWLEDGED' ? 'active' : 'inactive'}`} onClick={() => setFilter('ACKNOWLEDGED')} style={{ borderLeft: filter === 'ACKNOWLEDGED' ? 'none' : '4px solid #0ea5e9' }}>
                  Acknowledged
                </div>
                <div className={`filter-tab ${filter === 'READY_FOR_VERIFICATION' ? 'active' : 'inactive'}`} onClick={() => setFilter('READY_FOR_VERIFICATION')} style={{ borderLeft: filter === 'READY_FOR_VERIFICATION' ? 'none' : '4px solid #f59e0b' }}>
                  Ready to Verify
                </div>
                <div className={`filter-tab ${filter === 'VERIFIED_CLOSED' ? 'active' : 'inactive'}`} onClick={() => setFilter('VERIFIED_CLOSED')} style={{ borderLeft: filter === 'VERIFIED_CLOSED' ? 'none' : '4px solid #10b981' }}>
                  Closed History
                </div>
              </div>

              <div style={{ position: "relative", minWidth: "250px", maxWidth: "300px" }}>
                <i className="bi bi-search" style={{ position: "absolute", left: "14px", top: "11px", color: "#64748b" }}></i>
                <input 
                  type="text" 
                  placeholder="Search Lot or Batch..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ 
                    padding: "10px 10px 10px 42px", width: "100%", borderRadius: "10px", 
                    border: "1px solid #cbd5e1", outline: "none", fontSize: "14px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)", backgroundColor: "#fff"
                  }}
                  onFocus={e => e.target.style.borderColor = "#0ea5e9"}
                  onBlur={e => e.target.style.borderColor = "#cbd5e1"}
                />
              </div>
            </div>

            {/* ── TABLE CARD ── */}
            <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)", overflow: "hidden", border: "1px solid #e2e8f0" }}>
              
              {loading ? (
                <div style={{ padding: "60px 0", textAlign: "center" }}>
                  <div className="spinner-border text-danger" style={{ width: "3rem", height: "3rem" }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p style={{ marginTop: 16, color: "#64748b", fontWeight: 500 }}>Fetching tickets...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table wip-ticket-table mb-0">
                    <thead>
                      <tr>
                        <th>Ticket ID</th>
                        <th>Lot / Batch</th>
                        <th>Status & SLA</th>
                        <th>Defect Details</th>
                        <th>Qty (Exp/Act)</th>
                        <th>Responsible</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTickets.length === 0 ? (
                        <tr>
                          <td colSpan="7" style={{ padding: "60px 0", textAlign: "center" }}>
                            <div style={{ background: "#f1f5f9", width: 64, height: 64, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                              <i className="bi bi-inbox text-slate-400" style={{ fontSize: 28, color: "#94a3b8" }}></i>
                            </div>
                            <h5 style={{ color: "#334155", fontWeight: 600 }}>No tickets found</h5>
                            <p style={{ color: "#64748b", fontSize: 14 }}>There are no tickets matching your current filter.</p>
                          </td>
                        </tr>
                      ) : (
                        filteredTickets.map(t => {
                          const badge = badgeClass(t.status);
                          return (
                            <tr key={t.ticket_id}>
                              <td style={{ fontWeight: 700, color: "#0f172a" }}>#{t.ticket_id}</td>
                              <td>
                                <div style={{ fontWeight: 600, color: "#0f172a" }}>{t.lot_id}</div>
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{t.batch_id}</div>
                              </td>
                              <td>
                                <span style={{ 
                                  display: "inline-block", padding: "4px 10px", borderRadius: "6px", 
                                  fontSize: "12px", fontWeight: 700, backgroundColor: badge.bg, color: badge.color 
                                }}>
                                  {t.status.replace(/_/g, ' ')}
                                </span>
                                <div style={{ fontSize: 12, color: "#0ea5e9", fontWeight: 600, marginTop: 4 }}>
                                  Due: {new Date(t.due_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                </div>
                              </td>
                              <td>
                                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                                  {t.media_url ? (
                                    <img 
                                      src={`${BASE_URL}${t.media_url.startsWith('/wip_tickets') ? '/uploads' + t.media_url : t.media_url}`} 
                                      alt="Evidence" 
                                      style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, border: "1px solid #e2e8f0" }} 
                                    />
                                  ) : (
                                    <div style={{ width: 48, height: 48, borderRadius: 6, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                      <i className="bi bi-image text-muted"></i>
                                    </div>
                                  )}
                                  <div>
                                    <div style={{ fontWeight: 600, color: "#334155" }}>{t.defect_category}</div>
                                    <div style={{ fontSize: 13, color: "#64748b", marginTop: 2, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {t.comments || "No comments"}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <span style={{ fontWeight: 600, color: "#334155" }}>{t.expected_qty}</span>
                                  <span style={{ color: "#cbd5e1" }}>/</span>
                                  <span style={{ fontWeight: 700, color: t.actual_qty < t.expected_qty ? "#e11d48" : "#334155" }}>{t.actual_qty}</span>
                                </div>
                              </td>
                              <td>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontWeight: 600, fontSize: 11 }}>
                                    {t.responsible_supervisor?.substring(0,2).toUpperCase() || "?"}
                                  </div>
                                  <span style={{ fontWeight: 500, color: "#334155", fontSize: 14 }}>{t.responsible_supervisor || "Unassigned"}</span>
                                </div>
                              </td>
                              <td className="text-end">
                                <div className="d-flex justify-content-end gap-2">
                                  {t.status === 'TEMPORARY' && (
                                    <button onClick={() => handleAction(t.ticket_id, 'acknowledge')} 
                                      className="btn btn-sm" 
                                      style={{ background: "#fff", color: "#0ea5e9", border: "1px solid #0ea5e9", fontWeight: 600, borderRadius: 6, padding: "6px 12px" }}>
                                      Acknowledge
                                    </button>
                                  )}
                                  {(t.status === 'ACKNOWLEDGED' || t.status === 'IN_PROGRESS') && (
                                    <button onClick={() => handleAction(t.ticket_id, 'ready')} 
                                      className="btn btn-sm" 
                                      style={{ background: "#f59e0b", color: "#fff", border: "none", fontWeight: 600, borderRadius: 6, padding: "6px 12px", boxShadow: "0 2px 4px rgba(245, 158, 11, 0.2)" }}>
                                      Mark Ready
                                    </button>
                                  )}
                                  {t.status === 'READY_FOR_VERIFICATION' && (
                                    <button onClick={() => handleAction(t.ticket_id, 'verify')} 
                                      className="btn btn-sm" 
                                      style={{ background: "#10b981", color: "#fff", border: "none", fontWeight: 600, borderRadius: 6, padding: "6px 12px", boxShadow: "0 2px 4px rgba(16, 185, 129, 0.2)" }}>
                                      Verify & Close
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}
